import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PENDING_SALE_ORDERS_QUERY } from '@/modules/cashier-pos/cashier-pos.gql';
import { customerLabel, normalizeSaleOrder, petLabel, type SaleOrder } from '@/modules/cashier-pos/sale-order-shared';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog';

interface PendingResponse {
  pendingSaleOrders: { items: SaleOrder[] };
}

interface OpeningAndCorrectionsModalProps {
  on_close: () => void;
  can_switch: boolean;
  on_select: (sale_order_id: string) => void;
  on_new: () => void;
}

/**
 * The single-station equivalent of the two-station "Órdenes en apertura" panel
 * (`CashierPosPage.tsx`'s `opening_queue_orders` — same filter, same query), moved
 * behind a button so the main window stays focused on the active sale.
 */
export function OpeningAndCorrectionsModal({
  on_close,
  can_switch,
  on_select,
  on_new,
}: OpeningAndCorrectionsModalProps): JSX.Element {
  const { t } = useTranslation();
  const { data, loading } = useQuery<PendingResponse>(PENDING_SALE_ORDERS_QUERY, {
    variables: {
      offset: 0,
      limit: 50,
      customer_id: null,
      pet_id: null,
      folio_number: null,
      veterinarian_id: null,
      include_sent_to_doctor_pending_take: false,
    },
    fetchPolicy: 'cache-and-network',
  });

  const orders = useMemo(
    () =>
      (data?.pendingSaleOrders.items ?? [])
        .map(normalizeSaleOrder)
        .filter(
          (order) =>
            (order.status === 'created_by_cashier' && !order.appointment_id) ||
            order.status === 'returned_to_cashier_for_correction',
        ),
    [data],
  );

  return (
    <Dialog open onOpenChange={(open) => !open && on_close()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('single_station_pos.opening_modal_title')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Button type="button" onClick={on_new} className="w-full">
            {t('single_station_pos.new_sale_button')}
          </Button>
          {loading && orders.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('common.loading')}</p>
          ) : null}
          {!loading && orders.length === 0 ? (
            <p className="text-muted-foreground py-4 text-center text-sm">
              {t('cashier_pos.opening_orders_empty')}
            </p>
          ) : null}
          <div className="space-y-2">
            {orders.map((order) => (
              <Card
                key={order.id}
                role="button"
                tabIndex={can_switch ? 0 : -1}
                onClick={() => can_switch && on_select(order.id)}
                onKeyDown={(event) => {
                  if (can_switch && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    on_select(order.id);
                  }
                }}
                className={`${
                  can_switch ? 'cursor-pointer hover:border-primary/50' : 'cursor-not-allowed opacity-60'
                } border border-border`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-sm">{customerLabel(order.customer_snapshot_json)}</CardTitle>
                    <Badge variant="muted">{petLabel(order.pet_snapshot_json)}</Badge>
                  </div>
                </CardHeader>
                <CardContent className="text-muted-foreground text-xs">
                  {t(`doctor_pos.status_${order.status}`)}
                </CardContent>
              </Card>
            ))}
          </div>
          {!can_switch ? (
            <p className="text-muted-foreground text-xs">
              {t('single_station_pos.cannot_switch_hint')}
            </p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
